import { useEffect, useState } from 'react';
import { getDisplayTeamBg } from '../displayTheme';

type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface BondOptionResult {
  id: string;
  text: string;
  resolvedText: string;
  votes: number;
  percentage: number;
}

interface BondResultsDisplayProps {
  voteId: string;
  activeTeam?: TeamColor;
}

function BondResultsDisplay({ voteId, activeTeam }: BondResultsDisplayProps) {
  const [options, setOptions] = useState<BondOptionResult[]>([]);
  const [winningOptionId, setWinningOptionId] = useState<string | null>(null);
  const [fromTeam, setFromTeam] = useState<TeamColor | null>(null);
  const [heroName, setHeroName] = useState<string>('');
  const [totalVotes, setTotalVotes] = useState(0);

  // Fetch bond vote results
  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(`/api/bond-votes/${voteId}/results`);
        if (response.ok) {
          const data = await response.json();
          setOptions(data.options || []);
          setWinningOptionId(data.winningOptionId);
          setFromTeam(data.fromTeam);
          setTotalVotes(data.totalVotes || 0);
        }
      } catch (error) {
        console.error('[BondResultsDisplay] Error fetching results:', error);
      }
    };

    fetchResults();
    // Poll for updates
    const interval = setInterval(fetchResults, 2000);
    return () => clearInterval(interval);
  }, [voteId]);

  // Fetch hero name
  useEffect(() => {
    const fetchHeroName = async () => {
      if (!fromTeam) return;

      try {
        const response = await fetch(`/api/bond-votes/${voteId}`);
        if (response.ok) {
          const data = await response.json();
          const sessionId = data.bondVote.sessionId;

          // Get the winning sheet from sheet votes
          const votesResponse = await fetch(`/api/sheet-votes/session/${sessionId}/latest`);
          if (votesResponse.ok) {
            const votesData = await votesResponse.json();
            const teamVote = votesData.votes?.find((v: any) => v.teamColor === fromTeam && v.finalSheetId);
            if (teamVote?.winnerSheet?.name) {
              setHeroName(teamVote.winnerSheet.name);
            }
          }
        }
      } catch (error) {
        console.error('[BondResultsDisplay] Error fetching hero:', error);
      }
    };

    fetchHeroName();
  }, [voteId, fromTeam]);

  const team = activeTeam || fromTeam;
  const bgClass = getDisplayTeamBg(team);

  return (
    <div className={`h-screen w-screen flex flex-col items-center justify-center p-12 ${bgClass}`}>
      {/* Titolo */}
      <div className="mb-8 text-center">
        <h1 className="text-5xl font-bold text-parchment-100 font-display mb-2">Risultati Legame</h1>
        {heroName && (
          <p className="text-3xl text-parchment-200 font-heading">{heroName}</p>
        )}
        <p className="text-xl text-parchment-400 font-heading mt-2">{totalVotes} voti totali</p>
      </div>

      {/* Opzioni con barre percentuali */}
      <div className="w-full max-w-4xl space-y-4">
        {options.map((option) => {
          const isWinner = option.id === winningOptionId;

          return (
            <div
              key={option.id}
              className={`rounded-2xl p-6 border-2 transition-all ${
                isWinner
                  ? 'bg-yellow-500/20 border-yellow-400'
                  : 'display-panel'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <p className={`text-2xl font-semibold ${isWinner ? 'text-yellow-300 font-heading' : 'text-parchment-100 font-heading'}`}>
                  {option.resolvedText}
                </p>
                <span className={`text-3xl font-bold ${isWinner ? 'text-yellow-300 font-heading' : 'text-parchment-400 font-heading'}`}>
                  {option.percentage}%
                </span>
              </div>
              <div className="h-4 bg-parchment-500/30 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    isWinner ? 'bg-yellow-400' : 'bg-parchment-300'
                  }`}
                  style={{ width: `${option.percentage}%` }}
                />
              </div>
              <p className="text-parchment-400 text-xl mt-1">{option.votes} voti</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BondResultsDisplay;
